import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import { Icon } from 'assets/icons';
import { ReviewActions } from '../actions';
import styles from '../view.module.css';

const FIELD_LABELS = {
  body: 'Message',
  cta: 'CTA',
  hook: 'Hook',
  intro: 'Intro',
  link: 'Link',
  message: 'Message',
  preview_text: 'Preview text',
  secondary: 'Secondary',
  subject_line: 'Subject line',
  summary: 'Summary',
  tease: 'Tease',
  text: 'Message',
  title: 'Title',
  transition: 'Transition'
};

const FIELD_ORDER = [
  'title',
  'subject_line',
  'preview_text',
  'hook',
  'intro',
  'message',
  'body',
  'text',
  'summary',
  'transition',
  'tease',
  'secondary',
  'cta',
  'link'
];

const SHORT_FIELDS = ['title', 'subject_line', 'preview_text', 'link'];

const toLabel = (key) =>
  FIELD_LABELS[key] || key.replace(/_/g, ' ').replace(/^./, (letter) => letter.toUpperCase());

const resolveSource = (output = {}) =>
  output.sections && typeof output.sections === 'object' ? output.sections : output;

const resolveKeys = (output = {}) => {
  const source = resolveSource(output);
  return Object.keys(source)
    .filter((key) => typeof source[key] === 'string')
    .sort((a, b) => {
      const a_rank = FIELD_ORDER.indexOf(a);
      const b_rank = FIELD_ORDER.indexOf(b);
      if (a_rank === -1 && b_rank === -1) return a.localeCompare(b);
      if (a_rank === -1) return 1;
      if (b_rank === -1) return -1;
      return a_rank - b_rank;
    });
};

const buildDraft = (output = {}) => {
  const source = resolveSource(output);
  return resolveKeys(output).reduce((draft, key) => ({ ...draft, [key]: source[key] || '' }), {});
};

export const WhatsappJobReview = ({
  content_job = {},
  output = {},
  read_only = false,
  acting = null,
  onApprove = () => {},
  onSaveDraft = () => {},
  onReject = () => {}
}) => {
  const [draft, setDraft] = useState(() => buildDraft(output));

  useEffect(() => {
    setDraft(buildDraft(output));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content_job.id, content_job.current_version, content_job.edited_output]);

  const pristine_draft = useMemo(() => buildDraft(output), [output]);
  const has_changes = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(pristine_draft),
    [draft, pristine_draft]
  );

  const field_keys = useMemo(() => resolveKeys(output), [output]);
  const uses_sections = resolveSource(output) !== output;

  const assembled_message = field_keys
    .filter((key) => !SHORT_FIELDS.includes(key))
    .map((key) => draft[key])
    .filter(Boolean)
    .join('\n\n');

  const buildOutput = () =>
    uses_sections
      ? { ...output, sections: { ...(output.sections || {}), ...draft } }
      : { ...output, ...draft };

  const handleFieldChange = (key, value) => {
    setDraft((previous_draft) => ({ ...previous_draft, [key]: value }));
  };

  const handleCopy = async () => {
    if (!assembled_message) return;

    try {
      await navigator.clipboard.writeText(assembled_message);
      toast.success('Message copied');
    } catch (e) {
      toast.error('Unable to copy. Select the text and copy it by hand.');
    }
  };

  if (field_keys.length === 0) {
    return (
      <section className={styles.card}>
        <p className={styles.previewLabel}>Agent output</p>
        <pre className={styles.rawOutput}>{JSON.stringify(output, null, 2)}</pre>
      </section>
    );
  }

  return (
    <>
      <div className={styles.splitPane}>
        <div className={styles.splitColumn}>
          {field_keys.map((key) => (
            <section key={key} className={styles.card}>
              <label className={styles.fieldLabel} htmlFor={`content-job-whatsapp-${key}`}>
                {toLabel(key)}
              </label>
              {SHORT_FIELDS.includes(key) ? (
                <input
                  id={`content-job-whatsapp-${key}`}
                  className={styles.textInput}
                  value={draft[key]}
                  onChange={(e) => handleFieldChange(key, e.target.value)}
                  disabled={read_only}
                  placeholder={toLabel(key)}
                />
              ) : (
                <textarea
                  id={`content-job-whatsapp-${key}`}
                  className={styles.textArea}
                  value={draft[key]}
                  onChange={(e) => handleFieldChange(key, e.target.value)}
                  disabled={read_only}
                  placeholder={`${toLabel(key)} copy`}
                  rows={4}
                />
              )}
            </section>
          ))}
        </div>

        <div className={styles.splitColumn}>
          <section className={`${styles.card} ${styles.previewCard}`}>
            <div className={styles.cardHead}>
              <p className={styles.previewLabel}>Live preview</p>
              <button
                type="button"
                className={styles.copyBtn}
                onClick={handleCopy}
                disabled={!assembled_message}
              >
                <Icon name="copy" width={14} height={14} />
                <span>Copy</span>
              </button>
            </div>
            <div className={styles.waPreview}>
              <div className={styles.waBubble}>
                {assembled_message ? (
                  <p className={styles.waText}>{assembled_message}</p>
                ) : (
                  <p className={styles.waPlaceholder}>Message body</p>
                )}
                <span className={styles.waTime}>12:00</span>
              </div>
            </div>
          </section>
        </div>
      </div>

      {!read_only && (
        <ReviewActions
          can_save_draft={has_changes}
          acting={acting}
          onApprove={() => onApprove(has_changes ? buildOutput() : null)}
          onSaveDraft={() => onSaveDraft(buildOutput())}
          onReject={onReject}
        />
      )}
    </>
  );
};
